/**
 * WHO MADE WHAT, FROM THE MANIFEST AND NOTHING ELSE.
 *
 * `gauntlet/assets.manifest.json` is the asset authority: every shipped file has a creator
 * and a licence there, and every reference the art was measured against is ruled on there
 * too. This module is the small, shippable transcription of it - the manifest is 119 kB and
 * does not belong in the bundle - and `credits.test.ts` holds every figure and every name
 * below against the manifest and against `package.json`, so a credit here that the manifest
 * does not back is a failing test rather than an invented line.
 */

export interface CreditLine {
  readonly what: string;
  readonly who: string;
  readonly licence: string;
}

/** Shipped art: counted by manifest category among `runtime-approved` rows. */
export const SHIPPED_ART: readonly { readonly category: string; readonly count: number; readonly said: string }[] = [
  { category: 'authored island', count: 40, said: 'islands' },
  { category: 'fish plate', count: 24, said: 'fish plates' },
  { category: 'player vessel', count: 1, said: 'boat' },
  { category: 'open-water material', count: 1, said: 'open-water tile' },
  { category: 'UI material', count: 14, said: 'pieces of interface material, including six character portraits and three outfitter previews' },
  { category: 'boat fitting', count: 3, said: 'earned-coin boat fittings' },
];

export const ART_CREDIT: CreditLine = { what: 'Boat, islands, fish plates and interface art', who: 'Reelin project', licence: 'Project asset' };

/** bake-water.mjs copies and adapts the CC0 source pixels; these are shipped art, not study-only. */
export const WATER_CREDIT: CreditLine = { what: 'Open-water tile, adapted for Reelin', who: 'POKOMOKO (Jason Wild); adaptation by Reelin project', licence: 'CC0 1.0' };

export const MURRIN_CREDIT: CreditLine = { what: 'Murrin fallback depiction', who: 'Reelin project; built-in OpenAI image generation', licence: 'Project asset; generated source and preparation recorded in docs/release/murrin-fallback-provenance.md' };

export const PORTRAIT_CREDIT: CreditLine = { what: 'Harbourmaster portrait', who: 'Reelin project; generated with SDXL base 1.0 and nerijs/pixel-art-xl', licence: 'Project asset; generated model terms and exact source recorded in docs/release/guide-portrait-provenance.md' };

export const PAPER_CREDIT: CreditLine = { what: 'Illustrated paper stock', who: 'Reelin project; built-in OpenAI image generation', licence: 'Project asset; source and prompt recorded in gauntlet/ui/paper-stock-20260906/provenance.json' };

export const OUTFITTER_ART_CREDIT: CreditLine = { what: 'Four fisher portraits and the outfitter keeper', who: 'Reelin project; generated with SDXL base 1.0 and nerijs/pixel-art-xl', licence: 'Project asset; generated sources and exports recorded in docs/release/outfitter-art-provenance.md' };
export const FITTINGS_CREDIT: CreditLine = { what: 'Boat fittings and their menu previews', who: 'Reelin project; built-in OpenAI image generation', licence: 'Project asset; generated sources and exports recorded in docs/release/outfitter-art-provenance.md' };

/** The one runtime dependency in the artefact. Version and licence from its package. */
export const CODE: readonly (CreditLine & { readonly version: string })[] = [
  { what: 'pixi.js', version: '8.20.1', who: 'PixiJS contributors', licence: 'MIT' },
];

export const BUILT_WITH: readonly { readonly pkg: string; readonly said: string }[] = [{ pkg: 'vite', said: 'Vite' }, { pkg: 'typescript', said: 'TypeScript' }];

/** Study-only references. The POKOMOKO source is credited separately because its adapted pixels ship. */
export const REFERENCES: readonly (CreditLine & { readonly id: string; readonly ruling: string })[] = [
  { id: 'eastward-water-00', what: 'Eastward shoreline capture', who: 'Pixpil / Chucklefish', licence: 'Copyrighted store capture held for private measurement', ruling: 'Eastward carries shoreline and contact-rim treatment only.' },
  { id: 'windward-behavior', what: 'Windward water behaviour', who: 'Tasharen Entertainment', licence: 'Project evidence', ruling: 'Windward governs water behavior, never pixel placement.' },
];

export const TORSK_CREDIT: CreditLine = { what: 'Torsk Vard snowy fishing village', who: 'Reelin project; built-in OpenAI image generation; transparent export and authored berth by Christabel', licence: 'Project asset; source, prompt and export retained in gauntlet/island/torsk-vard-20260910' };
